import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import Footer from '@zocdoc-frontend-common/components-footer';
import Header from '@zocdoc-frontend-common/components-header';
import Page from '@zocdoc-frontend-common/components-page';
import { SignInFormPatientProviderMode } from '@zocdoc-frontend-common/components-sign-in-form/components/SignInFormPatientProviderMode';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { buildSsoLoginUrl as buildSsoReturnUrl } from '@zocdoc-frontend-common/utils-react/buildSsoLoginUrl';
import { white } from '@zocdoc-mezzanine/foundations-colors';
import { Metrics, MetricsProvider } from '@zocdoc/frontend-metrics';
import { getSSOLoginUrl } from 'src/accounts/utils';
import logToDataDog from 'src/config/logToDataDog';
import { getPageId } from 'src/utils/getPageId';

const SignIn = ({
    location,
    signedIn,
    socialLoginValidationError,
    showFacebookLoginButton,
    showGoogleLoginButton,
    showAppleLoginButton,
    showRememberMe,
    suggestedRedirect,
    rememberMe,
    returnUrl,
    showProviderMode,
}) => {
    useEffect(() => {
        if (signedIn) {
            logToDataDog({
                name: 'booking-web-app.account.account_signin_success',
                type: 'increment',
                value: 1,
                tags: [
                    `redirectUrl:${Boolean(returnUrl || suggestedRedirect)}`,
                ],
            });
            window.location.replace(returnUrl || suggestedRedirect);
        }
    }, [returnUrl, signedIn, suggestedRedirect]);

    const googleLoginUrl = buildSsoReturnUrl(
        getSSOLoginUrl('Google', location),
        returnUrl,
        rememberMe,
        showRememberMe
    );
    const facebookLoginUrl = buildSsoReturnUrl(
        getSSOLoginUrl('Facebook', location),
        returnUrl,
        rememberMe,
        showRememberMe
    );
    const appleLoginUrl = buildSsoReturnUrl(
        getSSOLoginUrl('Apple', location),
        returnUrl,
        rememberMe,
        showRememberMe
    );

    const apiHost = process.env.API_HOST || '';
    const endpoint = `${apiHost}/api/2/web/user/authenticate`;

    const onClickCreateAccount = useCallback(() => {
        window.location.replace('/createuser/details');
    }, []);

    const signInFormProps = {
        allowToggle: true,
        autoFocusEmailField: true,
        facebookLoginUrl,
        googleLoginUrl,
        appleLoginUrl,
        initialMode: showProviderMode ? 'provider' : 'patient',
        onClickCreateAccount,
        returnUrl,
        showFacebookLogin: showFacebookLoginButton,
        showGoogleLogin: showGoogleLoginButton,
        showAppleLogin: showAppleLoginButton,
        showRememberMe,
        rememberMe,
        socialLoginValidationError,
        url: endpoint,
    };

    return (
        <MetricsProvider getPageId={getPageId}>
            <Metrics
                businessUnit="PatientWebApp"
                category={'SignIn'}
                page={'SignIn'}
                zlogPageType={'SignIn'}
                zlogRollupBucket={'Utility'}
            >
                <Page>
                    <Header />
                    <Container>
                        <Column>
                            <StyledSignInForm
                                data-test="sign-in-form-patient-provider-mode"
                                {...signInFormProps}
                            />
                        </Column>
                    </Container>
                    <Footer />
                </Page>
            </Metrics>
        </MetricsProvider>
    );
};

const Container = styled.div`
    display: flex;
    justify-content: center;
    text-align: center;
    flex-direction: column;
    background-color: ${white};
`;

const Column = styled.div`
    width: 350px;
    margin: 47px auto 0;

    ${breakpoint.small} {
        width: 512px;
        margin: 88px auto 264px;
    }
`;

const StyledSignInForm = styled(SignInFormPatientProviderMode)`
    text-align: left;
`;

export default SignIn;
